import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Send, ShieldCheck, Zap, Tag } from "lucide-react";
import HirafiIcon from "@/components/HirafiIcon";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedNavigate, useLocalizedPath } from "@/hooks/useLocalizedNavigate";

const InscriptionClient = () => {
  const { t, locale } = useLanguage("auth");
  const lNavigate = useLocalizedNavigate();
  const lp = useLocalizedPath();

  useSEO({
    title: t("inscription_client.title"),
    description: locale === "ar"
      ? "أنشئ حسابك مجانًا على حرافي وانشر طلبات أعمالك. احصل على عروض من سبّاكين وكهربائيين وبنّائين موثّقين في جميع ولايات الجزائر."
      : "Créez votre compte gratuitement sur Hirafi et publiez vos demandes de travaux. Recevez des propositions de plombiers, électriciens et maçons vérifiés dans toutes les wilayas d'Algérie.",
    canonical: "/inscription-client",
    locale,
  });

  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");

  const benefits = [
    { icon: ShieldCheck, text: t("benefits.verified") },
    { icon: Zap,         text: t("benefits.fast_search") },
    { icon: Tag,         text: t("benefits.free_long") },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({ title: "Erreur", description: t("inscription_client.error_passwords"), variant: "destructive" });
      return;
    }
    if (password.length < 6) {
      toast({ title: "Erreur", description: t("inscription_client.error_too_short"), variant: "destructive" });
      return;
    }
    if (!displayName.trim()) {
      toast({ title: "Erreur", description: t("inscription_client.error_name"), variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const nameParts = displayName.trim().split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;

      toast({
        title: t("inscription_client.success"),
        description: t("inscription_client.success_desc"),
      });
      lNavigate(redirectTo ? `/connexion?redirect=${encodeURIComponent(redirectTo)}` : "/connexion");
    } catch (error: any) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left decoration panel — desktop only */}
      <div className="hidden lg:flex lg:w-2/5 xl:w-1/2 auth-decoration flex-col justify-between p-10 xl:p-14">
        <Link to={lp("/")} className="flex items-center gap-2.5" aria-label="Hirafi — Accueil">
          <HirafiIcon size={36} className="opacity-90" />
          <span className="font-display text-xl font-bold text-white tracking-tight">Hirafi</span>
        </Link>

        <div className="space-y-8">
          <div className="space-y-3">
            <h2 className="font-display text-3xl xl:text-4xl font-bold text-white leading-tight">
              {t("inscription_client.decoration_title")}
            </h2>
            <p className="text-white/70 text-lg leading-relaxed">
              {t("inscription_client.decoration_subtitle")}
            </p>
          </div>
          <ul className="space-y-4">
            {benefits.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3 text-white/85">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/15">
                  <Icon className="h-4 w-4 text-white" aria-hidden="true" />
                </div>
                <span className="text-sm font-medium">{text}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-white/40 text-xs">© 2026 Hirafi · Tous droits réservés</p>
      </div>

      {/* Right form panel */}
      <div className="flex flex-1 flex-col justify-center px-5 py-12 sm:px-8 lg:px-12 xl:px-16">
        {/* Mobile logo */}
        <div className="lg:hidden mb-8">
          <Link to={lp("/")} className="flex items-center gap-2.5" aria-label="Hirafi — Accueil">
            <HirafiIcon size={36} />
            <span className="font-display text-xl font-bold tracking-tight">Hirafi</span>
          </Link>
        </div>

        <div className="w-full max-w-md mx-auto">
          <div className="mb-8">
            <h1 className="font-display text-2xl font-bold md:text-3xl">{t("inscription_client.title")}</h1>
            <p className="mt-1.5 text-muted-foreground">{t("inscription_client.subtitle")}</p>
          </div>

          <div className="space-y-4 mb-4">
            <GoogleAuthButton label={t("inscription_client.google")} />
            <div className="relative flex items-center gap-3">
              <div className="flex-1 border-t border-border" />
              <span className="text-xs text-muted-foreground">{t("inscription_client.or")}</span>
              <div className="flex-1 border-t border-border" />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="display-name" className="font-display font-semibold text-sm">{t("inscription_client.display_name")}</Label>
              <Input
                id="display-name"
                placeholder={t("inscription_client.name_placeholder")}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                autoComplete="name"
                className="h-11"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email" className="font-display font-semibold text-sm">{t("inscription_client.email_label")}</Label>
              <Input
                id="email"
                type="email"
                placeholder="votre@email.dz"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="h-11"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="password" className="font-display font-semibold text-sm">{t("inscription_client.password_label")}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="h-11"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password" className="font-display font-semibold text-sm">{t("inscription_client.confirm_label")}</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="h-11"
                />
              </div>
            </div>

            <Button variant="hero" size="xl" className="w-full mt-2 gap-2" type="submit" disabled={loading}>
              <Send className="h-5 w-5" />
              {loading ? t("inscription_client.loading") : t("inscription_client.submit")}
            </Button>
          </form>

          <div className="mt-6 space-y-2 text-center text-sm text-muted-foreground border-t pt-6">
            <p>
              {t("inscription_client.artisan_q")}{" "}
              <Link to={lp("/inscription")} className="font-semibold text-primary hover:underline underline-offset-4">
                {t("inscription_client.artisan_link")}
              </Link>
            </p>
            <p>
              {t("inscription_client.already_account")}{" "}
              <Link to={redirectTo ? lp(`/connexion?redirect=${encodeURIComponent(redirectTo)}`) : lp("/connexion")} className="font-semibold text-primary hover:underline underline-offset-4">
                {t("inscription_client.login")}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InscriptionClient;
